// First: under Playwright the dev server stays silent (devE2eMute.ts).
import "./devE2eMute";
// Second: browser errors to Sentry, a no-op without a DSN (boot/errorReporting.ts).
import "./boot/errorReportingSpa";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { setClientVersion } from "./api";
import { NativeUpdateHost } from "./lifecycle/NativeUpdateHost";
import { installAppLinks } from "./native/appLinks";
import { hideSplashAfterFirstPaint } from "./boot/splash";
import { createPlatformAdapter } from "./platform";
import { setImageShare } from "./platform/imageShare";
import { installNativeAuthStorage } from "./platform/secureStorage";
import { installKeyboardInset } from "./chrome/keyboardInset";
import { bootProjectSound } from "./audio/boot";
import { installStaleChunkReload } from "./boot/staleChunk";
import { ChunkErrorBoundary } from "./boot/ChunkErrorBoundary";
import "./chrome/fonts.css";
import "./chrome/tokens.css";
import "./chrome/global.css";

function render() {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <ChunkErrorBoundary>
        <App />
      </ChunkErrorBoundary>
      <NativeUpdateHost />
    </React.StrictMode>,
  );
  // capacitor.config sets launchAutoHide: false, so this is what ends the splash.
  hideSplashAfterFirstPaint(createPlatformAdapter().hideSplash);
}

// A tab left open across a deploy reloads once for the chunks it lost (staleChunk.ts).
installStaleChunkReload();

// Native only: the software keyboard never covers the field being typed into (13.6).
void installKeyboardInset().catch(() => {});
// Project sound: make and arm the host's AudioContext before any frame exists, so the
// first tap on our own chrome unlocks it (sound-and-music contract §5).
bootProjectSound();
// Photo save goes through the platform: straight to Photos where the shell has WBMPhotos
// (so no grown-up check, U18), else the native share sheet. The share page's island, which
// never runs this, keeps the web share (platform/imageShare.ts).
setImageShare((image, prefer) => createPlatformAdapter().shareImage(image, prefer), {
  savesDirectly: createPlatformAdapter().canSaveToPhotos(),
});

// Native only: name the installed build on every API request (R15.1). Started
// alongside the Keychain read, never in front of render; requests that go out
// before it resolves simply carry no header.
void createPlatformAdapter().getAppVersion().then(setClientVersion, () => {});

// Native only: a universal link to one of our hosts lands where the same link
// would on the web (R16.2, native/appLinks.ts). The web adapter's listener is a
// no-op — the browser already navigated.
{
  const adapter = createPlatformAdapter();
  installAppLinks(adapter.onAppUrlOpen.bind(adapter));
}

// Native only: read the Keychain refresh token before the auth provider's first
// refresh (ipad-native-shell 2.3). Resolves immediately on web and never rejects.
void installNativeAuthStorage().finally(render);
